import { Fade } from "@mui/material";
import { Box } from "@mui/material";
import { CenterMapItem, Clear, Eye } from "@procore/core-icons";
import { Button, colors, Typography } from "@procore/core-react";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { EventOrigin } from "apis/procore";
import { useAppSelector } from "app/redux-store-interactions";
import { EnhancedTooltip } from "components/toolbars/tooltips";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { PICKERS } from "features/hud/coachmarkPickers";
import { selectPicker } from "features/render";
import { useAbortController } from "hooks/useAbortController";
import IsolateIcon from "media/icons/isolate.svg?react";
import XRayIcon from "media/icons/xray.svg?react";
import { selectIsProcoreDesign, selectIsProcoreMobileDesign } from "slices/explorer";
import { telemetry } from "telemetry";
import { getObjectData } from "utils/search";

import { useSelectionActions } from "./useSelectionActions";

// The bar sits at the same vertical offset as the corner toolbars (DetailsMenu /
// QuickAccessMenu). It is center-positioned so there is no horizontal overlap.
export const SELECTION_BAR_TOP = 8;

/**
 * Thin guard: only renders the bar on Procore web (not mobile, not standalone).
 */
export function SelectionBar() {
    const isProcoreDesign = useAppSelector(selectIsProcoreDesign);
    const isMobileDesign = useAppSelector(selectIsProcoreMobileDesign);
    const activePicker = useAppSelector(selectPicker);

    if (!isProcoreDesign || isMobileDesign || PICKERS.includes(activePicker)) {
        return null;
    }

    return <SelectionBarContent />;
}

function SelectionBarContent() {
    const { t } = useTranslation();
    const {
        selectedCount,
        highlightedIds,
        mainObjects,
        hide: hideAction,
        isolate: isolateAction,
        isolateXray: isolateXrayAction,
        zoomToSelection: zoomAction,
        areAllHidden,
        isXrayActive,
        toggleXray: handleXrayClick,
        deselectAll: handleClose,
        deselectAllDisabled,
    } = useSelectionActions(EventOrigin.SelectionBar);
    const {
        state: { db, view },
    } = useExplorerGlobals();

    // Single-object name fetching — mirrors useProcoreStamp pattern
    const [singleObjectName, setSingleObjectName] = useState<string | null>(null);
    const [isLoadingName, setIsLoadingName] = useState(false);
    const [isNameError, setIsNameError] = useState(false);
    const [abortController, abort] = useAbortController();

    // Controls whether the bar is visible at all.
    // Stays false until we have a name loaded for single-object selections,
    // so "1 object selected" is never shown as a transient label.
    const [isReadyToShow, setIsReadyToShow] = useState(false);

    const isSingleSelection = selectedCount === 1;
    // For name fetch: prefer the resolved mainObject ID (mainObjects[0]), fall back to the first
    // highlighted ID for the paths that leave mainObjects empty.
    const singleObjectId = isSingleSelection ? (mainObjects[0] ?? highlightedIds[0]) : undefined;

    useEffect(() => {
        abort();

        if (singleObjectId === undefined || !db || !view) {
            setSingleObjectName(null);
            setIsLoadingName(false);
            setIsNameError(false);
            return;
        }

        const signal = abortController.current.signal;
        setIsNameError(false);
        setIsLoadingName(true);

        (async () => {
            try {
                const loaded = await getObjectData({ db, id: singleObjectId, view });
                if (!signal.aborted) {
                    const decoded = (() => {
                        try {
                            return decodeURIComponent(loaded?.name ?? "");
                        } catch {
                            return loaded?.name ?? null;
                        }
                    })();
                    // Use "" (not null) for "fetched but no name" — null strictly means "not yet loaded".
                    // This lets isReadyToShow use `!== null` to mean "fetch has settled".
                    setSingleObjectName(decoded && decoded.length > 0 ? decoded : "");
                }
            } catch (e) {
                if (!signal.aborted) {
                    telemetry.trackCaughtException({
                        source: "selectionBar.loadObjectName",
                        category: "data",
                        error: e,
                    });
                    setSingleObjectName(null);
                    setIsNameError(true);
                }
            } finally {
                if (!signal.aborted) {
                    setIsLoadingName(false);
                }
            }
        })();
    }, [singleObjectId, db, view, abort, abortController]);

    // Gate bar visibility:
    //  - 0 selected  → reset to hidden
    //  - >1 selected → show immediately (no name fetch needed)
    //  - 1 selected, name loaded → show
    //  - 1 selected, still loading → no-op (keeps previous value)
    //    • 0→1 transition: stays false until name arrives (hides "1 object selected")
    //    • 1→1 switch: stays true (bar visible with dimmed previous name)
    useEffect(() => {
        if (selectedCount === 0) {
            setIsReadyToShow(false);
        } else if (selectedCount > 1) {
            setIsReadyToShow(true);
        } else if (selectedCount === 1 && !isLoadingName && singleObjectName !== null) {
            // Covers both a real name (non-empty string) and "" (fetched but no name).
            // null strictly means "not yet loaded" — bar stays hidden until fetch settles.
            setIsReadyToShow(true);
        } else if (selectedCount === 1 && !isLoadingName && isNameError) {
            setIsReadyToShow(true);
        }
        // selectedCount === 1 && isLoadingName: intentionally no-op (keeps bar hidden on 0→1)
    }, [selectedCount, isLoadingName, singleObjectName, isNameError]);

    const labelText =
        isSingleSelection && !isLoadingName
            ? singleObjectName || t("selectionBarObjectsSelected", { count: 1 })
            : t("selectionBarObjectsSelected", { count: selectedCount });

    // Committed-label pattern: keep showing the last fully-loaded label
    // (dimmed) while a new single-object name is still in-flight.
    const committedLabelRef = useRef<string>("");

    useEffect(() => {
        if (!isLoadingName) {
            committedLabelRef.current = labelText;
        }
    }, [isLoadingName, labelText]);

    const displayedLabel =
        isSingleSelection && isLoadingName && committedLabelRef.current !== "" ? committedLabelRef.current : labelText;

    const withBlur = (handler?: () => void) => (e: React.MouseEvent<HTMLElement>) => {
        e.currentTarget.blur();
        handler?.();
    };

    const isVisible = highlightedIds.length > 0;

    return (
        <Fade in={isVisible && isReadyToShow} unmountOnExit timeout={{ enter: 150, exit: 0 }}>
            {/* Neutral wrapper — Fade injects style={{ opacity: 1 }} on its direct child as an
                inline style, which would override any sx/style-based opacity on that node.
                This wrapper absorbs Fade's inline style; the styled Box below controls its own
                opacity so the entire bar (background, shadow, and content) dims during loading. */}
            <div>
                <Box
                    role="toolbar"
                    aria-label={t("selectionBarLabel")}
                    sx={{
                        background: colors.white,
                        borderRadius: "8px",
                        boxShadow: "0px 4px 12px 0px rgba(0,0,0,0.2)",
                        minWidth: 240,
                        maxWidth: 480,
                        // Parent HUD container has pointerEvents: none to keep the canvas
                        // interactive; restore auto here so the bar's buttons work.
                        pointerEvents: "auto",
                        pl: "16px",
                        pr: "12px",
                        py: "8px",
                        gap: "16px",
                        display: "flex",
                        alignItems: "center",
                    }}
                >
                    {/* Label — dims during single-object name loading */}
                    <div
                        style={{
                            flex: "1 0 0",
                            minWidth: 0,
                            cursor: "default",
                            opacity: isLoadingName && isSingleSelection ? 0.5 : 1,
                            transition: "opacity 0.15s ease",
                        }}
                    >
                        {isNameError ? (
                            <Typography
                                intent="small"
                                weight="semibold"
                                style={{
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    display: "block",
                                    lineHeight: 1,
                                    color: colors.red40,
                                }}
                            >
                                {t("selectionBarNameError")}
                            </Typography>
                        ) : (
                            <div style={{ overflow: "hidden" }}>
                                <Typography
                                    intent="small"
                                    weight="semibold"
                                    color="gray15"
                                    style={{
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        display: "block",
                                        lineHeight: 1,
                                    }}
                                >
                                    {displayedLabel}
                                </Typography>
                            </div>
                        )}
                    </div>

                    {/* Action buttons + close grouped so their inter-group gap (8px) is
                    independent of the label→buttons gap (12px on the outer Box) */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: "8px", flexShrink: 0 }}>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                            }}
                        >
                            {/* Hide / Unhide — Eye / EyeOff are core icons */}
                            <EnhancedTooltip
                                config={{
                                    toolName: "hide",
                                    shortcutKey: "",
                                    label: areAllHidden ? t("selectionBarUnhide") : t("selectionBarHide"),
                                    placement: "bottom",
                                }}
                                showOnHover
                            >
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    icon={<Eye size="sm" />}
                                    aria-label={areAllHidden ? t("selectionBarUnhide") : t("selectionBarHide")}
                                    disabled={hideAction?.disabled || (isLoadingName && isSingleSelection)}
                                    onClick={withBlur(hideAction?.onClick)}
                                />
                            </EnhancedTooltip>

                            {/* Isolate — no core icon; using isolate.svg from media/icons */}
                            <EnhancedTooltip
                                config={{
                                    toolName: "isolate",
                                    shortcutKey: "",
                                    label: isolateAction?.label ?? "",
                                    placement: "bottom",
                                }}
                                showOnHover
                            >
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    icon={<IsolateIcon width={16} height={16} />}
                                    aria-label={isolateAction?.label ?? ""}
                                    disabled={isolateAction?.disabled || (isLoadingName && isSingleSelection)}
                                    onClick={withBlur(isolateAction?.onClick)}
                                />
                            </EnhancedTooltip>

                            {/* Isolate with X-ray — no core icon; using xray.svg from media/icons */}
                            <Box
                                sx={
                                    isXrayActive
                                        ? {
                                              display: "contents",
                                              "& button": {
                                                  backgroundColor: `${colors.blue94} !important`,
                                                  "&:hover": { backgroundColor: `${colors.blue94} !important` },
                                              },
                                          }
                                        : { display: "contents" }
                                }
                            >
                                <EnhancedTooltip
                                    config={{
                                        toolName: "isolate-xray",
                                        shortcutKey: "",
                                        label: isXrayActive
                                            ? t("unisolateFromXRayMode")
                                            : (isolateXrayAction?.label ?? ""),
                                        placement: "bottom",
                                    }}
                                    showOnHover
                                >
                                    <Button
                                        variant="secondary"
                                        size="sm"
                                        icon={
                                            <XRayIcon
                                                width={16}
                                                height={16}
                                                style={{ color: isXrayActive ? colors.blue50 : undefined }}
                                            />
                                        }
                                        aria-label={
                                            isXrayActive ? t("unisolateFromXRayMode") : (isolateXrayAction?.label ?? "")
                                        }
                                        disabled={isolateXrayAction?.disabled || (isLoadingName && isSingleSelection)}
                                        onClick={withBlur(handleXrayClick)}
                                    />
                                </EnhancedTooltip>
                            </Box>

                            {/* Zoom to Selection — CenterMapItem is the closest core icon */}
                            <EnhancedTooltip
                                config={{
                                    toolName: "zoom",
                                    shortcutKey: "",
                                    label: zoomAction?.label ?? "",
                                    placement: "bottom",
                                }}
                                showOnHover
                            >
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    icon={<CenterMapItem size="sm" />}
                                    aria-label={zoomAction?.label ?? ""}
                                    disabled={zoomAction?.disabled || (isLoadingName && isSingleSelection)}
                                    onClick={withBlur(zoomAction?.onClick)}
                                />
                            </EnhancedTooltip>
                        </Box>

                        {/* Close — Clear is a core icon */}
                        <EnhancedTooltip
                            config={{
                                toolName: "deselect-all",
                                shortcutKey: "",
                                label: t("selectionBarDeselectAll"),
                                placement: "bottom",
                            }}
                            showOnHover
                        >
                            <Button
                                variant="tertiary"
                                size="sm"
                                icon={<Clear size="sm" />}
                                aria-label={t("selectionBarDeselectAll")}
                                disabled={deselectAllDisabled}
                                onClick={withBlur(handleClose)}
                            />
                        </EnhancedTooltip>
                    </Box>
                </Box>
            </div>
        </Fade>
    );
}
